import { useMemo } from 'react';
import { StatusBar } from '@weareopenr/component-library';
import { useAuthWrapper } from '@weareopenr/authkit';
import { IPublishFormValues, Steps } from '../../types';
import { IntegrationType, FEATURE_FLAGS } from '../../../../constants';
import { VenueAllocationForm } from 'src/components/VenueAllocationModal';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import { useVenues } from 'src/hooks/useVenues/useVenues';
import { useScheduleConflicts } from 'src/hooks/useScheduleConflicts';
import { getVenuesForIntegrationType } from 'src/helpers/getVenuesForIntegrationType/getVenuesForIntegrationType';

const CONFLICT_NOTICE = (
  <>
    <strong>Quick note -</strong> Some venues are unavailable due to scheduling conflicts.
  </>
);

export const SelectVenues = ({
  submitDataToParent,
  defaultValues,
}: {
  submitDataToParent: (data: Partial<IPublishFormValues>, nextStep?: Steps) => void;
  defaultValues: Pick<IPublishFormValues, 'venues' | 'priceSchemeId' | 'publicationDate'>;
}) => {
  const { data: allVenues = [], isLoading: isVenuesLoading } = useVenues();
  const { nestedCategories, orderedFlatCategories, isLoading: isCategoriesLoading } = useVenueCategories();

  const { featureFlags } = useAuthWrapper();
  const enhancedScheduling = featureFlags.includes(FEATURE_FLAGS.ENHANCED_SCHEDULING);

  const { conflictingVenueIds, isLoading: isConflictsLoading } = useScheduleConflicts(
    defaultValues.publicationDate,
    enhancedScheduling,
  );

  // Filter venues to only show those from the POS system
  const posVenues = useMemo(
    () => getVenuesForIntegrationType(allVenues, IntegrationType.POINT_OF_SALE_MANAGEMENT),
    [allVenues],
  );

  // Only venues that are both shown here and have a conflict get disabled.
  const disabledVenueIds = useMemo(
    () => posVenues.map((venue) => venue.venue_id).filter((id) => conflictingVenueIds.has(id)),
    [posVenues, conflictingVenueIds],
  );

  // Never carry a conflicting venue forward as a pre-selected choice.
  const selectedVenueIds = useMemo(
    () => defaultValues.venues.filter((id) => !conflictingVenueIds.has(id)),
    [defaultValues.venues, conflictingVenueIds],
  );

  const handleVenueSubmit = ({ venueIds }: { categoryId: string; venueIds: string[] }) => {
    submitDataToParent({ venues: venueIds }, Steps.excludeProducts);
  };

  const handleBack = () => {
    submitDataToParent({}, Steps.instance);
  };

  const isLoading = isVenuesLoading || isCategoriesLoading || isConflictsLoading;

  return (
    <VenueAllocationForm
      key={isConflictsLoading ? 'loading' : 'ready'}
      entityId="price-scheme"
      entityName="Price Scheme"
      venues={posVenues}
      selectedVenueIds={selectedVenueIds}
      disabledVenueIds={disabledVenueIds}
      notice={disabledVenueIds.length > 0 && <StatusBar variant="info" message={CONFLICT_NOTICE} />}
      onModalClose={handleBack}
      onSubmit={handleVenueSubmit}
      isLoading={isLoading}
      venueCategories={orderedFlatCategories || []}
      nestedVenueCategories={nestedCategories || []}
      title="Price Scheme Destination"
      subtitle="Choose which venues will receive the pricing data from the selected price scheme. Venue selection applies only to pricing."
      cancelButtonLabel="Back"
      submitButtonLabel="Continue"
      disableSubmitWhenEmpty
    />
  );
};
